import type { ScenarioDelta } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { isOrgPersonNodeType } from '../models/defaults';
import {
  normalizeOrgPersonFieldValue,
  orgPersonWritableField,
} from '../fields/orgPersonWritableFields';

/**
 * Commit bridge (jobs-first plan, Phase 3): translate a client-side canvas
 * `ScenarioDelta` into canonical position-structure intent cores, so a scenario
 * edited on a data-bound diagram can commit to the org model through the
 * existing `OrgPlanningChangeSetService` apply path.
 *
 * This is the pure translation half only. The caller (the commit orchestrator)
 * wraps each core with transport fields (intentId, expectedGraphVersion,
 * effectiveAt, scenario ids), stages them sequentially into one scenario
 * change-set, then applies it.
 *
 * Scope: field edits (title/role/department) → `edit`, reporting moves →
 * `reparent`, new nodes → `create`, and deleted nodes → `retire`. Edge ops are
 * reported as `unsupported` rather than silently dropped, so the UI can say
 * "N of M changes committed."
 */

/** A position intent minus the transport/concurrency fields the caller fills in. */
export type PositionIntentCore =
  | { kind: 'edit'; positionId: string; title?: string; department?: string; level?: string }
  | { kind: 'reparent'; positionId: string; managerPositionId: string | null }
  | { kind: 'add_secondary_reporting'; positionId: string; managerPositionId: string; relationshipType: 'matrix_reports' }
  | {
      kind: 'create';
      title: string;
      department?: string;
      level?: string;
      managerPositionId: string | null;
      /** Person-card additions are hires: the position AND its occupant apply together. */
      hire?: { name: string; email?: string; fields?: Record<string, string> };
    }
  | { kind: 'retire'; positionId: string; reportingPolicy: 'retire_all' | 'reparent_direct_reports' }
  /**
   * A person record that holds no position — the Unassigned Employee card an
   * enacted transition leaves behind. There is no position to retire; this
   * stages as a `retire_person` structure intent so the removal lives in the
   * same scenario change-set as everything else and applies with it.
   */
  | { kind: 'retire_person'; personRecordId: string }
  /** Person fields staged into the same reviewed organization plan. */
  | {
      kind: 'update_person';
      personRecordId: string;
      fields: Record<string, string>;
    };

export interface UnsupportedDeltaOp {
  readonly op: 'addition' | 'removal' | 'edge-addition' | 'edge-removal' | 'update' | 'move';
  readonly nodeId: string;
  readonly reason: string;
}

/** Current canonical position a diagram node is bound to (via its folder record). */
export interface ResolvedPosition {
  readonly id: string;
  readonly title: string;
  readonly department?: string;
}

export interface ScenarioDeltaTranslationInput {
  readonly delta: ScenarioDelta;
  /** Diagram node id → the folder record it is bound to (`dataBinding.folderRecordId`). */
  readonly recordIdByNodeId: (nodeId: string) => string | undefined;
  /** Folder record id → the canonical position it occupies (with current values). */
  readonly positionByRecordId: (recordId: string) => ResolvedPosition | undefined;
  /** Folder record id → whether it is a canonical Person record. Enables position-less person removals. */
  readonly isPersonRecord?: (recordId: string) => boolean;
}

export interface ScenarioDeltaTranslation {
  readonly intents: readonly PositionIntentCore[];
  readonly unsupported: readonly UnsupportedDeltaOp[];
}

const DEPARTMENT_NODE_TYPE = 'org_department';
const DEPARTMENT_ADDITION_REASON = 'department cards are not committable';

/** User-facing explanation when a draft cannot commit because it adds a department card. */
export function departmentDraftMessage(unsupported: readonly UnsupportedDeltaOp[]): string | null {
  return unsupported.some((op) => op.op === 'addition' && op.reason === DEPARTMENT_ADDITION_REASON)
    ? 'Department cards can’t be saved to the organization. Departments come from each position’s Department value, so set the department on the positions instead, then remove the department card.'
    : null;
}

const TITLE_FIELDS = new Set(['title', 'role']);
/** Position fields that may be cleared, unlike the title. */
const OPTIONAL_POSITION_FIELDS = new Set(['department', 'level']);
/** Hire identity carried outside `hire.fields`. */
const HIRE_IDENTITY_FIELDS = new Set(['name', 'email']);

export function scenarioDeltaToPositionIntents(
  input: ScenarioDeltaTranslationInput,
): ScenarioDeltaTranslation {
  const {
    delta,
    recordIdByNodeId,
    positionByRecordId,
    isPersonRecord,
  } = input;
  const intents: PositionIntentCore[] = [];
  const unsupported: UnsupportedDeltaOp[] = [];
  const personUpdatesByRecordId = new Map<string, Record<string, string>>();
  const positionEditsById = new Map<string, Extract<PositionIntentCore, { kind: 'edit' }>>();

  const resolvePosition = (nodeId: string): ResolvedPosition | undefined => {
    const recordId = recordIdByNodeId(nodeId);
    return recordId ? positionByRecordId(recordId) : undefined;
  };

  const stagePersonFieldUpdate = (nodeId: string, field: string, newValue: unknown): boolean => {
    const manifest = orgPersonWritableField(field);
    if (!manifest || typeof newValue !== 'string') return false;
    const recordId = recordIdByNodeId(nodeId);
    if (!recordId || !isPersonRecord?.(recordId)) return false;
    const staged = personUpdatesByRecordId.get(recordId) ?? {};
    staged[manifest.id] = normalizeOrgPersonFieldValue(newValue);
    personUpdatesByRecordId.set(recordId, staged);
    return true;
  };

  const addedNodeIds = new Set(delta.additions.map((addition) => String(addition.id)));
  const addedPersonNodeIds = new Set(delta.additions
    .filter((addition) => isOrgPersonNodeType(addition.type))
    .map((addition) => String(addition.id)));
  // Edits to a node added in this same delta have no position to patch yet;
  // they fold into that node's create below, last write winning.
  const additionEditsByNodeId = new Map<string, {
    title?: string; department?: string; level?: string; name?: string; person?: Record<string, string>;
  }>();

  // Field edits are patches: never resend an unedited canonical field over
  // a previously saved draft value. Person-record
  // fields (name/email/dates) become merged Person facts in the same plan.
  for (const update of delta.updates) {
    const nodeId = String(update.nodeId);
    const field = update.change?.field;
    // A new card has no Person record yet; its person fields belong to the hire.
    if (field && addedNodeIds.has(nodeId) && typeof update.change.newValue === 'string') {
      const edits = additionEditsByNodeId.get(nodeId) ?? {};
      if (field === 'name') {
        edits.name = update.change.newValue.trim();
        additionEditsByNodeId.set(nodeId, edits);
        continue;
      }
      if (addedPersonNodeIds.has(nodeId) && orgPersonWritableField(field)) {
        edits.person = { ...edits.person, [field]: normalizeOrgPersonFieldValue(update.change.newValue) };
        additionEditsByNodeId.set(nodeId, edits);
        continue;
      }
    }
    if (!field || (!TITLE_FIELDS.has(field) && !OPTIONAL_POSITION_FIELDS.has(field))) {
      if (field && stagePersonFieldUpdate(nodeId, field, update.change.newValue)) continue;
      unsupported.push({ op: 'update', nodeId, reason: `field "${field ?? '?'}" is not committable yet` });
      continue;
    }
    const nextValue = typeof update.change.newValue === 'string' ? update.change.newValue.trim() : '';
    const positionField = TITLE_FIELDS.has(field) ? 'title' : field as 'department' | 'level';
    if (addedNodeIds.has(nodeId)) {
      const edits = additionEditsByNodeId.get(nodeId) ?? {};
      edits[positionField] = nextValue;
      additionEditsByNodeId.set(nodeId, edits);
      continue;
    }
    const position = resolvePosition(nodeId);
    if (!position) {
      unsupported.push({ op: 'update', nodeId, reason: 'node is not bound to a canonical position' });
      continue;
    }
    const staged = positionEditsById.get(position.id);
    if (positionField === 'title' && !nextValue) {
      unsupported.push({ op: 'update', nodeId, reason: 'position has no title to edit' });
      continue;
    }
    const edit: Extract<PositionIntentCore, { kind: 'edit' }> = {
      kind: 'edit',
      positionId: position.id,
      ...staged,
      [positionField]: nextValue,
    };
    if (staged) {
      Object.assign(staged, edit);
    } else {
      positionEditsById.set(position.id, edit);
      intents.push(edit);
    }
  }

  // New nodes → create intents. "Add a direct report" emits an addition plus a
  // paired move carrying the manager for the same new node; the manager folds
  // into the create here and the paired move is skipped below rather than
  // failing to resolve a record the node does not have yet.
  const pairedMoveManagerByNodeId = new Map<string, string>();
  for (const move of delta.moves) {
    const nodeId = String(move.nodeId);
    if (addedNodeIds.has(nodeId) && move.newManagerId != null) {
      pairedMoveManagerByNodeId.set(nodeId, String(move.newManagerId));
    }
  }
  for (const addition of delta.additions) {
    const nodeId = String(addition.id);
    // A department card is not a seat. Sources hold departments as a column
    // on position/person rows, so there is no canonical create for it.
    if (addition.type === DEPARTMENT_NODE_TYPE) {
      unsupported.push({ op: 'addition', nodeId, reason: DEPARTMENT_ADDITION_REASON });
      continue;
    }
    const edits = additionEditsByNodeId.get(nodeId);
    const title = (edits?.title ?? addition.title ?? edits?.name ?? addition.name ?? '').trim();
    if (!title) {
      unsupported.push({ op: 'addition', nodeId, reason: 'new position needs a title' });
      continue;
    }
    const managerNodeId = addition.managerId != null
      ? String(addition.managerId)
      : pairedMoveManagerByNodeId.get(nodeId) ?? null;
    let managerPositionId: string | null = null;
    if (managerNodeId != null) {
      const managerPosition = resolvePosition(managerNodeId);
      if (!managerPosition) {
        unsupported.push({ op: 'addition', nodeId, reason: 'manager is not bound to a canonical position' });
        continue;
      }
      managerPositionId = managerPosition.id;
    }
    const department = edits?.department
      ?? (typeof addition.department === 'string' ? addition.department.trim() : '');
    const level = edits?.level ?? '';
    // A person-card addition is a hire: on apply the position AND the person
    // occupying it materialize together, so both charts update in one go.
    const personName = isOrgPersonNodeType(addition.type) ? (edits?.name ?? addition.name ?? '').trim() : '';
    const email = edits?.person?.email ?? (typeof (addition.props as { email?: unknown } | undefined)?.email === 'string'
      ? String((addition.props as { email?: string }).email).trim()
      : '');
    // A cleared field on a card that has no Person yet is simply unset.
    const hireFields = Object.fromEntries(Object.entries(edits?.person ?? {})
      .filter(([fieldId, value]) => value && !HIRE_IDENTITY_FIELDS.has(fieldId)));
    intents.push({
      kind: 'create',
      title,
      department: department || undefined,
      ...(level ? { level } : {}),
      managerPositionId,
      ...(personName ? { hire: {
        name: personName,
        ...(email ? { email } : {}),
        ...(Object.keys(hireFields).length ? { fields: hireFields } : {}),
      } } : {}),
    });
  }

  // Deleted nodes → retire intents. Direct reports are reparented rather than
  // retired with them: deleting one box on the canvas is not an instruction to
  // retire everyone underneath it.
  for (const removal of delta.removals) {
    const nodeId = String(removal.nodeId);
    const recordId = recordIdByNodeId(nodeId);
    // Card identity decides the operation. Occupancy must not turn a Person
    // retirement into a Position closure (which leaves that Person unassigned).
    if (recordId && isPersonRecord?.(recordId)) {
      intents.push({ kind: 'retire_person', personRecordId: recordId });
      continue;
    }
    const position = resolvePosition(nodeId);
    if (!position) {
      unsupported.push({ op: 'removal', nodeId, reason: 'node is not bound to a canonical position' });
      continue;
    }
    intents.push({ kind: 'retire', positionId: position.id, reportingPolicy: 'reparent_direct_reports' });
  }

  // Reporting moves → reparent intents. A null new manager reparents to root.
  for (const move of delta.moves) {
    const nodeId = String(move.nodeId);
    if (addedNodeIds.has(nodeId)) continue;
    const position = resolvePosition(nodeId);
    if (!position) {
      unsupported.push({ op: 'move', nodeId, reason: 'node is not bound to a canonical position' });
      continue;
    }
    let managerPositionId: string | null = null;
    if (move.newManagerId != null) {
      const managerPosition = resolvePosition(String(move.newManagerId));
      if (!managerPosition) {
        unsupported.push({ op: 'move', nodeId, reason: 'new manager is not bound to a canonical position' });
        continue;
      }
      managerPositionId = managerPosition.id;
    }
    intents.push({ kind: 'reparent', positionId: position.id, managerPositionId });
  }

  for (const [personRecordId, fields] of personUpdatesByRecordId) {
    intents.push({
      kind: 'update_person',
      personRecordId,
      fields,
    });
  }

  // Matrix reporting is a canonical secondary reporting relationship. It is
  // represented by a typed edge addition because, unlike a primary report,
  // it must not replace the Position's `reports_to` manager. Other connector
  // edits are not yet committable — surfaced so the caller can report partial
  // coverage. Reporting-line changes arrive as `moves`. Incident edges removed
  // as dependent state of a retired node are already covered by that retirement
  // intent.
  const removedNodeIds = new Set(delta.removals.map((removal) => String(removal.nodeId)));
  for (const edge of delta.edgeAdditions) {
    if (edge.type === 'matrix_reports') {
      const position = resolvePosition(String(edge.from));
      const manager = resolvePosition(String(edge.to));
      if (!position || !manager) {
        unsupported.push({ op: 'edge-addition', nodeId: String(edge.id), reason: 'matrix reporting endpoints must be bound to canonical positions' });
        continue;
      }
      intents.push({
        kind: 'add_secondary_reporting',
        positionId: position.id,
        managerPositionId: manager.id,
        relationshipType: 'matrix_reports',
      });
      continue;
    }
    unsupported.push({ op: 'edge-addition', nodeId: String(edge.id), reason: 'connector edits are not committable yet' });
  }
  for (const edge of delta.edgeRemovals) {
    if (edge.cascadeFromNodeId && removedNodeIds.has(String(edge.cascadeFromNodeId))) continue;
    unsupported.push({ op: 'edge-removal', nodeId: String(edge.edgeId), reason: 'connector edits are not committable yet' });
  }

  return { intents, unsupported };
}
